import { useState } from 'react';
import { Button } from 'components/core';
import Typho from '../../../core/OldComponents/Typho/Typho';
import { isPermitted, useGetUserQuery } from 'services/userApi';
import {
  BatchApprovalRule,
  useDeactivateBatchApprovalRuleMutation,
  useListBatchApprovalRulesQuery,
} from 'services/batchApprovalRulesApi';
import RuleFormModal from './RuleFormModal';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const fmtBound = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : currency.format(Number(n));

const fmtPropertyScope = (rule: BatchApprovalRule) => {
  if (!rule.property_ids || rule.property_ids.length === 0) return 'All properties';
  return `${rule.property_ids.length} propert${rule.property_ids.length === 1 ? 'y' : 'ies'}`;
};

const ApprovalRules = () => {
  const { data: user } = useGetUserQuery({});
  const canManage = isPermitted(user, 'approval-rules-manage');

  // Omit customer_id — backend falls back to req.user.customerId for the current auth context.
  const { data: rules, isLoading, isError, refetch } = useListBatchApprovalRulesQuery({});
  const [deactivate, { isLoading: isDeactivating }] = useDeactivateBatchApprovalRuleMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<BatchApprovalRule | null>(null);

  const openCreate = () => {
    setEditingRule(null);
    setModalOpen(true);
  };
  const openEdit = (rule: BatchApprovalRule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditingRule(null);
  };

  const handleDeactivate = async (rule: BatchApprovalRule) => {
    if (!window.confirm(`Deactivate rule "${rule.name}"?`)) return;
    await deactivate(rule.id);
    refetch();
  };

  return (
    <div className="flex flex-col w-full mb-8">
      <div className="flex items-start justify-between w-full max-w-[1024px] mb-6">
        <Typho size="14s" type="p" color="black" weight="light">
          Payment approval rules route incoming vendor payments to approvers based on amount ranges
          and optional property scope. Rules are opt-in — a payment with no matching rule bypasses
          the gate. These settings take effect once <code>requires_payment_approval</code> is
          enabled on the customer.
        </Typho>
        {canManage && (
          <Button
            variants="default"
            buttonLabel="Create rule"
            onClick={openCreate}
            additionalClasses="ml-4 whitespace-nowrap px-4 py-2"
          />
        )}
      </div>

      <div className="w-full max-w-[1024px]">
        {isLoading && <Typho type="p">Loading rules…</Typho>}
        {isError && (
          <Typho type="p" color="red">
            Failed to load approval rules.
          </Typho>
        )}
        {!isLoading && rules && rules.length === 0 && (
          <Typho type="p" color="grey">
            No approval rules configured yet.
          </Typho>
        )}
        {!isLoading && rules && rules.length > 0 && (
          <table className="w-full text-14s border border-grey-100 rounded-lg">
            <thead className="bg-grey-50 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Properties</th>
                <th className="px-4 py-3 font-medium">Min</th>
                <th className="px-4 py-3 font-medium">Max</th>
                <th className="px-4 py-3 font-medium">Approvers</th>
                <th className="px-4 py-3 font-medium">Status</th>
                {canManage && <th className="px-4 py-3 font-medium text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className="border-t border-grey-100">
                  <td className="px-4 py-3">
                    <div className="font-medium">{rule.name}</div>
                    {rule.approver_role_label && (
                      <div className="text-grey-600 text-12s">{rule.approver_role_label}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">{fmtPropertyScope(rule)}</td>
                  <td className="px-4 py-3">{fmtBound(rule.min_amount)}</td>
                  <td className="px-4 py-3">{fmtBound(rule.max_amount)}</td>
                  <td className="px-4 py-3">{rule.approver_user_ids?.length ?? 0}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded text-12s ${
                        rule.active ? 'bg-green-100 text-green-800' : 'bg-grey-100 text-grey-600'
                      }`}
                    >
                      {rule.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="text-blue-600 hover:underline mr-3"
                        onClick={() => openEdit(rule)}
                      >
                        Edit
                      </button>
                      {rule.active && (
                        <button
                          type="button"
                          className="text-red-600 hover:underline disabled:opacity-50"
                          disabled={isDeactivating}
                          onClick={() => handleDeactivate(rule)}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RuleFormModal
        isOpen={modalOpen}
        onClose={closeModal}
        editingRule={editingRule}
        onSuccess={() => {
          closeModal();
          refetch();
        }}
      />
    </div>
  );
};

export default ApprovalRules;
